<template>
  <div class="product-bundle-archive">
    <Loading :loading="isFetching" loading-text="Loading..." background="none-background">
      <div class="content-wrapper" :class="{ 'loading': isFetching }">
        <!-- Table -->
        <div class="table-wrapper">
          <div class="archive-table">
            <!-- Table Header - only show when there's data -->
            <div v-if="!isFetching && shortcutsItems.length > 0" class="table-header">
              <div class="th bundle-info">BUNDLE & DESCRIPTION</div>
              <div class="th divider"></div>
              <div class="th total-value">TOTAL VALUE</div>
              <div class="th divider"></div>
              <div class="th bundle-price">BUNDLE PRICE</div>
              <div class="th divider"></div>
              <div class="th actions">ACTIONS</div>
            </div>

            <!-- Table Body -->
            <div class="table-body">
              <!-- No data state -->
              <div v-if="!isFetching && shortcutsItems.length === 0" class="no-data-state">
                <img src="@/assets/MyWorkBench/archive/empty.svg" alt="Empty" class="empty-icon" />
                <p class="empty-text">You don't have any archived bundles.</p>
              </div>

              <!-- Data rows -->
              <div
                v-for="item in shortcutsItems"
                :key="item.shortcut_id"
                class="table-row"
                :class="{ 'row-loading': processingItemId === item.shortcut_id }"
              >
                <div v-if="processingItemId === item.shortcut_id" class="row-loading-overlay">
                  <ArrowSmall class="row-loading-icon" />
                </div>
                <div class="td bundle-info">
                  <div class="bundle-name">{{ item.shortcut_name }}</div>
                  <div v-if="item.description" class="bundle-description">{{ item.description }}</div>
                </div>
                <div class="td divider"></div>
                <div class="td total-value">{{ formatCurrency(item.total_value) }}</div>
                <div class="td divider"></div>
                <div class="td bundle-price">{{ formatCurrency(item.bundle_price) }}</div>
                <div class="td divider"></div>
                <div class="td actions">
                  <template v-if="processingItemId !== item.shortcut_id">
                    <button class="action-btn restore-btn" @click="handleUnarchiveShortcut(item)">
                      <img src="@/assets/MyWorkBench/archive/restore.svg" alt="Restore" class="action-icon" />
                      <span class="action-text">Restore</span>
                    </button>
                    <button class="action-btn delete-btn" @click="handleHideShortcut(item)">
                      <img src="@/assets/MyWorkBench/archive/delete.svg" alt="Hide" class="action-icon" />
                      <span class="action-text">Permanently Delete</span>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination at bottom -->
        <Pagination
          v-if="shortcutsItems.length > 0"
          :total_items="total"
          :current_page="currentPage"
          :per_page="perPage"
          :per_page_options="[10, 20, 30]"
          @update:current_page="handlePageChange"
          @update:per_page="handlePageSizeChange"
        />
      </div>
    </Loading>

    <DeleteConfirmDialog
      v-model="deleteDialogVisible"
      title="Delete Product Bundle"
      :item-name="selectedShortcut?.shortcut_name"
      :confirm-action="handleShortcutDelete"
      loading-text="Deleting bundle..."
      modal-class="delete-confirm-dialog"
    />
  </div>
</template>

<script>
import Loading from '@/NewLibrary/Loading/Loading.vue';
import ArrowSmall from '@/NewLibrary/Loading/ArrowSmall.vue'
import DeleteConfirmDialog from '@/views/my-workbench-new/archive/components/DeleteConfirmDialog.vue';

import { notify } from '@/utils/notify';
import {
  getArchivedShortcutsApi,
  unarchiveShortcutApi,
  hideShortcutApi,
} from '@/api/archive';

export default {
  name: 'ProductBundleArchive',
  components: {
    Loading,
    ArrowSmall,
    DeleteConfirmDialog,
  },
  props: {
    customerId: {
      type: [String, Number, null],
      default: null,
    },
    clinicId: {
      type: [String, Number, null],
      default: null,
    },
  },

  data() {
    return {
      isFetching: false,
      perPage: 10,
      currentPage: 1,
      shortcutsItems: [],
      total: 0,
      deleteDialogVisible: false,
      selectedShortcut: null,
      processingItemId: null,
    };
  },

  watch: {
    perPage() {
      this.fetchArchivedShortcuts();
    },
    currentPage() {
      this.fetchArchivedShortcuts();
    },
    customerId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchArchivedShortcuts();
        }
      },
    },
    clinicId: {
      handler() {
        if (this.customerId) {
          this.fetchArchivedShortcuts();
        }
      },
    },
  },

  methods: {
    async fetchArchivedShortcuts(showLoading = true) {
      if (!this.customerId) {
        console.warn('No customer ID available');
        return;
      }

      if (showLoading) this.isFetching = true;
      try {
        const params = {
          customer_id: this.customerId,
          page: this.currentPage,
          limit: this.perPage,
        };

        // Add clinic_id if available
        if (this.clinicId) {
          params.clinic_id = this.clinicId;
        }

        const response = await getArchivedShortcutsApi(params);
        this.shortcutsItems = response.data.shortcuts || [];
        this.total = response.data.total || 0;
      } catch (error) {
        console.error('Failed to fetch archived shortcuts:', error);
        this.shortcutsItems = [];
        this.total = 0;
      } finally {
        if (showLoading) this.isFetching = false;
      }
    },

    async handleUnarchiveShortcut(item) {
      if (this.processingItemId) return;
      try {
        this.processingItemId = item.shortcut_id;
        await unarchiveShortcutApi(item.shortcut_id);
        notify({ type: 'success', message: `"${item.shortcut_name}" has been restored` });
        await this.fetchArchivedShortcuts(false);
      } catch (error) {
        console.error('Failed to unarchive shortcut:', error);
        notify({ type: 'error', message: `Failed to restore "${item.shortcut_name}"` });
      } finally {
        this.processingItemId = null;
      }
    },

    handleHideShortcut(item) {
      this.selectedShortcut = item;
      this.deleteDialogVisible = true;
    },

    async handleShortcutDelete() {
      try {
        await hideShortcutApi(this.selectedShortcut.shortcut_id);
        notify({
          type: 'success',
          title: 'Bundle Deleted',
          message: `"${this.selectedShortcut.shortcut_name}" has been permanently deleted.`
        });
        await this.fetchArchivedShortcuts(false);
      } catch (error) {
        console.error('Error deleting shortcut:', error);
        notify({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete bundle. Please try again.'
        });
        throw error;
      } finally {
        this.selectedShortcut = null;
      }
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePageSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.product-bundle-archive {
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.content-wrapper.loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Table Wrapper */
.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.archive-table {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  border-radius: 4px;
  overflow: hidden;
}

/* Table Header */
.table-header {
  display: flex;
  align-items: center;
  background-color: #F2F2F2;
  padding: 4px 16px;
  border-radius: 5px 5px 0 0;
}

.table-header .th {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #374957;
  text-align: center;
  padding: 0 8px;
}

.table-header .th.bundle-info {
  flex: 2;
  text-align: center;
  min-width: 250px;
}

.table-header .th.total-value {
  flex: 1;
  text-align: center;
}

.table-header .th.bundle-price {
  flex: 1;
  text-align: center;
}

.table-header .th.divider {
  width: 1px;
  height: 24px;
  padding: 0;
  background: transparent;
}

.table-header .th.actions {
  flex: 1;
  text-align: center;
}

/* Table Body */
.table-body {
  min-height: 0;
  height: 100%;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.table-body::-webkit-scrollbar {
  width: 6px;
}

.table-body::-webkit-scrollbar-track {
  background: transparent;
}

.table-body::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.table-body::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  min-height: 100%;
  width: 100%;
  text-align: center;
}

:deep(.medication-pagination) {
  flex-shrink: 0;
}

.empty-icon {
  width: 350px;
  height: 230px;
}

.empty-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #333;
  margin: 0;
}

.table-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #E0E0E0;
  padding: 12px 16px;
  transition: background-color 0.2s ease;
  position: relative;
}

.table-row.row-loading {
  pointer-events: none;
}

.row-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  z-index: 1;
}

.row-loading-icon {
  width: 28px;
  height: 28px;
  transform-origin: center;
  animation: rotate 1.6s linear infinite;
}

.table-row:hover {
  background-color: #F9FAFB;
}

.table-row .td {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
  padding: 0 8px;
}

.table-row .td.bundle-info {
  flex: 2;
  text-align: left;
  min-width: 250px;
  overflow: hidden;
}

.bundle-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.bundle-description {
  font-size: 14px;
  font-weight: 400;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-row .td.total-value {
  flex: 1;
  text-align: center;
}

.table-row .td.bundle-price {
  flex: 1;
  text-align: center;
}

.table-row .td.divider {
  width: 1px;
  height: 24px;
  padding: 0;
}

.table-row .td.actions {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  white-space: nowrap;
  min-width: 24px;
  height: 32px;
}

.action-icon {
  flex-shrink: 0;
  display: block;
}

.action-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  transition: all 0.2s ease;
  /* height: 16px; */
}

.restore-btn:hover {
  background: rgba(0, 72, 121, 0.08);
  /* padding: 4px 8px; */
}

.restore-btn:hover .action-icon {
  filter: grayscale(1) brightness(0.5);
}

.restore-btn:hover .action-text {
  max-width: 180px;
  overflow: visible;
  opacity: 1;
  color: #1E4670;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  transition: all 0.8s ease;
}

/* Delete Permanently Button */

.delete-btn:hover {
  background: #FFEBEE;
  color: #D10000;
}

.delete-btn:hover .action-icon {
  filter: brightness(1.1);
}

.delete-btn:hover .action-text {
  max-width: 180px;
  overflow: visible;
  opacity: 1;
  color: #D10000;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px; 
  letter-spacing: 0.1px;
  transition: all 0.6s ease;
}

</style>
